import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';

// Helper function to convert PascalCase to camelCase
const toCamelCase = (str) => str.charAt(0).toLowerCase() + str.slice(1);

// Helper function to transform talent data from PascalCase to camelCase
const transformTalentData = (talent) => {
  const transformed = {};
  Object.entries(talent).forEach(([key, value]) => {
    // Special cases for field mapping
    if (key === 'Id') {
      transformed['talentId'] = value;
    } else if (key === 'CreatedByEmail') {
      transformed['createdBy'] = value;
    } else if (key === 'ModifiedByEmail') {
      transformed['modifiedBy'] = value;
    } else {
      transformed[toCamelCase(key)] = value;
    }
  });
  return transformed;
};

// Talent JSON Schema
const createEmptyTalent = () => ({
  talentId: null,  // Initialize as null to properly detect new talents
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  linkedInProfile: '',
  facebookPage: '',
  street1: '',
  street2: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
  talentCategory: '',
  rateRange: '',
  notes: '',
  status: 'Active',
  jobsHistory: [],  // Initialize empty array for job history
  createdBy: '',
  createdDate: '',
  modifiedBy: '',
  modifiedDate: ''
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  const result = await response.json();
  
  // If the response has multiple recordsets (e.g., talent and job history)
  if (Array.isArray(result.recordsets)) {
    const [talentData, jobHistoryData] = result.recordsets;
    if (talentData?.[0]) {
      const transformedTalent = transformTalentData(talentData[0]);
      transformedTalent.jobsHistory = jobHistoryData || [];
      return transformedTalent;
    }
    return null;
  }
  
  // Single recordset response
  return result;
};

export const talentService = {
  // Create empty talent
  createEmptyTalent,

  // Validate configuration (no-op for API-based services)
  validateConfiguration() {
    // Talent service uses backend API, no special configuration needed
    return true;
  },

  // Get all talents
  async getTalents(pageSize = 10000) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/talents?pageSize=${pageSize}&pageNumber=1`);
      const result = await handleResponse(response);
      // Transform each talent in the data array
      return (result.data || []).map(transformTalentData);
    } catch (error) {
      console.error('Error getting talents:', error);
      throw error;
    }
  },

  // Get talent by ID
  async getTalent(talentId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/talents/${talentId}`);
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get talent');
      }

      // Transform the main talent data
      const transformedTalent = {
        talentId: result.Id,
        firstName: result.FirstName,
        lastName: result.LastName,
        email: result.Email,
        phone: result.Phone,
        linkedInProfile: result.LinkedInProfile,
        facebookPage: result.FacebookPage,
        street1: result.Street1,
        street2: result.Street2,
        city: result.City,
        province: result.Province,
        country: result.Country,
        postalCode: result.PostalCode,
        talentCategory: result.TalentCategory,
        rateRange: result.RateRange,
        notes: result.Notes,
        status: result.Status,
        createdBy: result.CreatedByEmail,
        createdDate: result.CreatedDate,
        modifiedBy: result.ModifiedByEmail,
        modifiedDate: result.ModifiedDate
      };

      // Transform job history data if it exists
      if (result.jobsHistory && Array.isArray(result.jobsHistory)) {
        transformedTalent.jobsHistory = result.jobsHistory.map(job => ({
          id: job.Id,
          opportunityId: job.OpportunityId,
          opportunityName: job.OpportunityName,
          companyName: job.CompanyName,
          contactName: job.ContactName,
          jobDate: job.JobDate,
          rate: job.Rate,
          comment: job.Comment,
          createdDate: job.CreatedDate
        }));
      } else {
        transformedTalent.jobsHistory = [];
      }
      
      return transformedTalent;
    } catch (error) {
      console.error('Error getting talent:', error);
      throw error;
    }
  },

  // Save talent (create or update)
  async saveTalent(talent, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      // More explicit check for new talents
      const isNew = !talent.talentId || talent.talentId === null || talent.talentId === '';
      
      // For new talents, generate ID and set created info
      if (isNew) {
        talent.talentId = uuidv4();
        talent.createdBy = currentUserEmail;
        talent.createdDate = new Date().toISOString();
      }
      // Always set modified info for updates
      talent.modifiedBy = currentUserEmail;
      talent.modifiedDate = new Date().toISOString();

      // Map only the fields that the stored procedure expects
      const backendData = {
        id: talent.talentId,
        firstName: talent.firstName,
        lastName: talent.lastName,
        email: talent.email,
        phone: talent.phone,
        linkedInProfile: talent.linkedInProfile,
        facebookPage: talent.facebookPage,
        street1: talent.street1,
        street2: talent.street2,
        city: talent.city,
        province: talent.province,
        country: talent.country,
        postalCode: talent.postalCode,
        talentCategory: talent.talentCategory,
        rateRange: talent.rateRange,
        notes: talent.notes,
        status: talent.status,
        createdBy: isNew ? currentUserEmail : undefined,  // Only send for create
        modifiedBy: !isNew ? currentUserEmail : undefined // Only send for update
      };

      const method = isNew ? 'POST' : 'PUT';
      const url = isNew 
        ? `${apiBaseUrl}/talents`
        : `${apiBaseUrl}/talents/${talent.talentId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(backendData)
      });

      const result = await handleResponse(response);
      return transformTalentData(result);
    } catch (error) {
      console.error('Error saving talent:', error);
      throw error;
    }
  },

  // Delete talent
  async deleteTalent(talentId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/talents/${talentId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error deleting talent:', error);
      throw error;
    }
  },

  // Get talent categories from database
  async getTalentCategories() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/talents/categories`);
      
      if (response.ok) {
        const categories = await response.json();
        // Extract just the names from the database records
        return categories.map(category => category.Name);
      } else {
        // Fallback to default categories if database fails
        console.warn('Failed to get talent categories from database, using fallback');
        return [
          "CCTV Installation",
          "Digital Marketing",
          "Graphic Design",
          "Software Development", 
          "Wiring",
          "Other"
        ];
      }
    } catch (err) {
      console.error('Error getting talent categories from database:', err);
      // Fallback to default categories
      return [
        "CCTV Installation",
        "Digital Marketing",
        "Graphic Design",
        "Software Development", 
        "Wiring",
        "Other"
      ];
    }
  },

  // Helper function to format talent categories for display
  formatCategoriesForDisplay(categoriesString) {
    if (!categoriesString) return [];
    return categoriesString.split('|').filter(cat => cat.trim());
  },

  // Helper function to format talent categories for storage
  formatCategoriesForStorage(categoriesArray) {
    if (!categoriesArray || !Array.isArray(categoriesArray)) return '';
    return categoriesArray.join('|');
  },

  // Get active talents count
  async getActiveTalentsCount() {
    try {
      const talents = await this.getTalents();
      return talents.filter(talent => talent.status === 'Active').length;
    } catch (err) {
      console.error('Error getting active talents count:', err);
      return 0;
    }
  },

  // =============================================
  // JOB HISTORY METHODS
  // =============================================

  // Create job history
  async createJobHistory(talentId, jobData) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/talents/${talentId}/jobhistory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: jobData.id,
          talentId: talentId,
          opportunityId: jobData.opportunityId,
          jobDate: jobData.jobDate,
          rate: jobData.rate,
          comment: jobData.comment
        })
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Error creating job history:', error);
      throw error;
    }
  },

  // Update job history
  async updateJobHistory(jobHistoryId, jobData) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/talents/jobhistory/${jobHistoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          opportunityId: jobData.opportunityId,
          jobDate: jobData.jobDate,
          rate: jobData.rate,
          comment: jobData.comment
        })
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Error updating job history:', error);
      throw error;
    }
  },

  // Delete job history
  async deleteJobHistory(jobHistoryId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/talents/jobhistory/${jobHistoryId}`, {
        method: 'DELETE'
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Error deleting job history:', error);
      throw error;
    }
  },

  // Get job history by ID
  async getJobHistoryById(jobHistoryId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      
      const response = await fetch(`${apiBaseUrl}/talents/jobhistory/${jobHistoryId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Error getting job history:', error);
      throw error;
    }
  }
};
